/**
 * CRAD FDIC Large Bank — list / entry-point page
 *
 * Lists all Large Bank evaluations for the team and provides a "Run Large
 * Bank evaluation" action that creates a DRAFT report and navigates to its
 * detail page. Per CLARID-SPEC-CRAD-LARGE-001 §8 user flow step 1.
 *
 * The detail page (CradLargeBankPage) drives the rest of the evaluation —
 * tabs for Institution Ratings, Component & Composite Ratings, per-AA
 * test detail, peer comparison, and exports.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import { PlusIcon, ScaleIcon } from '@heroicons/react/24/outline';
import { cradLargeBankApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import { PageHeader, StatusBadge, LoadingSpinner } from '../components/ui';
import type { BadgeVariant } from '../components/ui';

const STATUS_BADGE: Record<string, BadgeVariant> = {
  DRAFT: 'neutral',
  IN_REVIEW: 'warning',
  FINAL: 'success',
};

const INSTITUTION_BADGE: Record<string, BadgeVariant> = {
  OUTSTANDING: 'success',
  SATISFACTORY: 'info',
  NEEDS_TO_IMPROVE: 'warning',
  SUBSTANTIAL_NONCOMPLIANCE: 'danger',
};

const INSTITUTION_LABEL: Record<string, string> = {
  OUTSTANDING: 'Outstanding',
  SATISFACTORY: 'Satisfactory',
  NEEDS_TO_IMPROVE: 'Needs to Improve',
  SUBSTANTIAL_NONCOMPLIANCE: 'Substantial Noncompliance',
};

interface LargeBankReportSummary {
  id: string;
  bankName: string;
  primaryRegulator: string;
  startDate: string;
  endDate: string;
  status: string;
  institutionRating: string | null;
  compositePoints: number | null;
  computedAt: string | null;
  createdAt: string;
  _count?: {
    stateRatings: number;
    testResults: number;
    peerBenchmarks: number;
    recommendations: number;
  };
}

export default function CradLargeBankListPage() {
  const { teamId } = useTeamId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['crad-large-bank', teamId],
    queryFn: () => cradLargeBankApi.list(teamId!),
    enabled: !!teamId,
  });

  const reports: LargeBankReportSummary[] = data?.data?.reports ?? [];

  const createMutation = useMutation({
    mutationFn: (body: {
      bankName: string;
      startDate: string;
      endDate: string;
      priorExamDate?: string;
      fdicCertNumber?: number;
    }) => cradLargeBankApi.create(teamId!, body),
    onSuccess: (res) => {
      toast.success('Large Bank evaluation created');
      queryClient.invalidateQueries({ queryKey: ['crad-large-bank'] });
      setCreateOpen(false);
      navigate(`/cra/large-bank/${res.data.id}`);
    },
    onError: () => toast.error('Failed to create evaluation'),
  });

  if (!teamId) return <LoadingSpinner size="lg" />;

  return (
    <FeatureGate feature="module_cra">
      <div className="space-y-6">
        <PageHeader
          title="CRAD — FDIC Large Bank"
          description="Three-test evaluation (Lending · Investment · Service) with point scoring, state rollups, and per-AA peer comparison."
          actions={
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500"
            >
              <PlusIcon className="h-4 w-4" />
              Run Large Bank evaluation
            </button>
          }
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <LoadingSpinner size="lg" />
          </div>
        ) : reports.length === 0 ? (
          <div className="rounded-lg border bg-white p-12 text-center">
            <ScaleIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Large Bank evaluations yet</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
              The Large Bank methodology applies to banks with $2B+ in assets and produces a three-test evaluation (Lending · Investment · Service) per FDIC procedures.
            </p>
            <button
              onClick={() => setCreateOpen(true)}
              className="mt-6 inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500"
            >
              <PlusIcon className="h-4 w-4" />
              Start your first evaluation
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Review Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rating
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Composite
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/cra/large-bank/${r.id}`}
                        className="text-sm font-medium text-teal-700 hover:text-teal-900"
                      >
                        {r.bankName}
                      </Link>
                      <p className="text-xs text-gray-500">{r.primaryRegulator}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                      {new Date(r.startDate).toLocaleDateString()} – {new Date(r.endDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      {r.institutionRating ? (
                        <StatusBadge variant={INSTITUTION_BADGE[r.institutionRating] || 'neutral'}>
                          {INSTITUTION_LABEL[r.institutionRating] ?? r.institutionRating}
                        </StatusBadge>
                      ) : (
                        <span className="text-xs text-gray-400">Not computed</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {r.compositePoints != null ? `${r.compositePoints} pts` : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge variant={STATUS_BADGE[r.status] || 'neutral'}>{r.status}</StatusBadge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <CreateReportDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSubmit={(body) => createMutation.mutate(body)}
          isPending={createMutation.isPending}
        />
      </div>
    </FeatureGate>
  );
}

function CreateReportDialog({
  open,
  onClose,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: {
    bankName: string;
    startDate: string;
    endDate: string;
    priorExamDate?: string;
    fdicCertNumber?: number;
  }) => void;
  isPending: boolean;
}) {
  const [bankName, setBankName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [priorExamDate, setPriorExamDate] = useState('');
  const [fdicCertNumber, setFdicCertNumber] = useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!bankName || !startDate || !endDate) return;
    onSubmit({
      bankName,
      startDate,
      endDate,
      priorExamDate: priorExamDate || undefined,
      fdicCertNumber: fdicCertNumber ? Number(fdicCertNumber) : undefined,
    });
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-semibold text-gray-900">
            New Large Bank evaluation
          </Dialog.Title>
          <p className="mt-1 text-sm text-gray-500">
            Creates a DRAFT report. Per spec, the review period begins the day after the prior exam date and ends at the end of the current evaluation window.
          </p>
          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Bank name</label>
              <input
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                placeholder="Planters Bank and Trust"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">FDIC Cert # (optional)</label>
              <input
                type="number"
                value={fdicCertNumber}
                onChange={(e) => setFdicCertNumber(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Period start</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Period end</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Prior exam date (optional)</label>
              <input
                type="date"
                value={priorExamDate}
                onChange={(e) => setPriorExamDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending}
                className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50"
              >
                {isPending ? 'Creating…' : 'Create evaluation'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
